import { get } from "lodash";
import Tooltip from "rc-tooltip";
import React from "react";
import CenteredLoading from "../../../components/CenteredLoading";
import HistogramChart from "../../../components/charts/HistogramChart";
import InfoIcon from "../../../components/icons/InfoIcon";
import { useFilteredList } from "../../../context/filteredList";
import { isHistogramDataLoading } from "../../../context/filteredList/FilteredListProvider";
import { Entity } from "../../../utils/constants";

interface Props {
  id: string;
  entity: Entity;
  title: string | React.ReactNode;
  metricIteratee: string;
}

const StatisticsCardWithRelativePerformance: React.FC<Props> = ({
  id,
  entity,
  title,
  metricIteratee,
  children
}) => {
  const {
    histogramDataMap,
    histogramBucketIndexMap,
    showRelativePerformance,
    toggleShowRelativePerformance
  } = useFilteredList();

  return (
    <div className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center justify-between">
          {title}
        </dt>

        <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white pb-12">
          {children}

          {showRelativePerformance && (
            <div className="w-full h-40">
              {isHistogramDataLoading(histogramDataMap, metricIteratee) ? (
                <CenteredLoading className="h-full" />
              ) : (
                <HistogramChart
                  data={histogramDataMap[metricIteratee]}
                  activeBucketIndex={get(
                    histogramBucketIndexMap,
                    [id, metricIteratee],
                    0
                  )}
                />
              )}
            </div>
          )}

          <div
            className="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6 cursor-pointer dark:bg-gray-700"
            onClick={() =>
              toggleShowRelativePerformance(entity, metricIteratee)
            }
          >
            <div className="text-sm font-medium text-hotspotty-500 hover:text-hotspotty-400 flex items-center dark:text-hotspotty-300 dark:hover:text-hotspotty-400">
              {showRelativePerformance ? "Hide" : "Show"} relative performance{" "}
              <Tooltip
                overlay={`Compare the performance of this ${entity} to others in this area. This is based on data points within the purple zone around the center of the map. When filtered labels are applied, only matching results are included.`}
              >
                <InfoIcon className="ml-1 inline-flex h-4 w-4" />
              </Tooltip>
            </div>
          </div>
        </dd>
      </div>
    </div>
  );
};

export default StatisticsCardWithRelativePerformance;
